import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import React, { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import useEventLogClinics from '../../hooks/useEventLogClinics';
import { EventLogClinic } from '../../services/eventLog.service';
import CheckboxDropdown from '../common/CheckboxDropdown';
import Header from '../layout/Header';

const PLAN_OPTIONS = [
  { label: 'EHR', value: 'ehr' },
  { label: 'Essentials', value: 'essentials' },
  { label: 'Trial', value: 'trial' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Canceled', value: 'canceled' },
  { label: 'Expired', value: 'expired' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Terminated', value: 'terminated' },
];

/**
 * Highlights matching text in a string. Returns JSX with <mark> tags.
 */
const HighlightText: React.FC<{ text: string; search: string }> = ({ text, search }) => {
  if (!search || !text) return <>{text}</>;

  const idx = text.toLowerCase().indexOf(search.toLowerCase());
  if (idx === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 rounded-sm px-0.5">
        {text.slice(idx, idx + search.length)}
      </mark>
      {text.slice(idx + search.length)}
    </>
  );
};

const EventLogClinics: React.FC = () => {
  const navigate = useNavigate();
  const { createToast } = useContext(ToastContext);
  const {
    data,
    isFetching,
    status,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    onSearch,
    onPlanFilterChange,
    onStatusFilterChange,
    searchQuery,
    planFilter,
    statusFilter,
  } = useEventLogClinics();

  // Show error toast when query fails
  React.useEffect(() => {
    if (status === 'error' && error) {
      createToast({
        title: 'Error loading clinics',
        description: (error as Error)?.message || 'Failed to load event log clinics',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  }, [status, error, createToast]);

  const allClinics = useMemo(() => {
    return data?.pages?.flatMap(page => page?.data || []) || [];
  }, [data]);

  const columns = useMemo<ColumnDef<EventLogClinic, unknown>[]>(
    () => [
      {
        accessorFn: row => row.clinicId,
        header: 'ID',
        cell: ({ row }) => (
          <HighlightText text={String(row.original.clinicId)} search={searchQuery} />
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'clinicName',
        header: 'Clinic',
        cell: ({ row }) => (
          <HighlightText
            text={row.original.clinicName || `Clinic ${row.original.clinicId}`}
            search={searchQuery}
          />
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'plan',
        header: 'Plan',
        cell: ({ row }) => <HighlightText text={row.original.plan || ''} search={searchQuery} />,
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <HighlightText text={row.original.status || ''} search={searchQuery} />,
        enableSorting: true,
      },
    ],
    [searchQuery]
  );

  return (
    <>
      <Header title="Event Logs" />
      <div className="p-6">
        <DataTable
          columns={columns}
          data={allClinics}
          size="default"
          onRowClick={row => navigate(`/event-logs/${row.clinicId}`)}
          striped
          loading={isFetching && !allClinics.length}
          emptyState={<p className="text-gray-500">No clinics with event data found</p>}
          toolbar={{
            search: {
              placeholder: 'Search...',
              value: searchQuery,
              onChange: onSearch,
            },
            rightSlot: (
              <div className="flex items-center gap-2">
                <CheckboxDropdown
                  title="Plan"
                  options={PLAN_OPTIONS}
                  selected={planFilter}
                  onChange={onPlanFilterChange}
                />
                <CheckboxDropdown
                  title="Status"
                  options={STATUS_OPTIONS}
                  selected={statusFilter}
                  onChange={onStatusFilterChange}
                />
              </div>
            ),
          }}
        />
        {hasNextPage && (
          <div className="mt-4 text-center">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {isFetchingNextPage ? 'Loading...' : 'Load more clinics'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default EventLogClinics;
